import React, { Fragment, useCallback, useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Container, Input, Label, FormGroup } from "reactstrap";
import Breadcrumb from "../common/breadcrumb";
import { RefreshCw, Eye, UploadCloud, Link2, AlertTriangle, RotateCcw } from "react-feather";
import { useSelector } from "react-redux";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Loader as RLoader } from "../reporting/ReportingShared";
import ToggleSwitch from "../../shared/ToggleSwitch";
import { errorToast } from "../../shared/Toast";
import {
  getGoogleBusinessStatus,
  getGoogleBusinessConnectUrl,
  importGoogleBusinessToken,
  updateGoogleBusinessSettings,
  disconnectGoogleBusiness,
  getGoogleBusinessLocations,
  autoMapGoogleLocations,
  previewGoogleMenu,
  getGoogleMenuRemote,
  pushGoogleMenuForBranch,
  pushAllGoogleMenus,
  getGoogleMenuPushLogs,
  getGoogleMenuBackups,
  restoreGoogleMenu,
} from "../../utils/apiCalls";

// Google Business menu sync control panel.
//
// The design brief for this page is one sentence: a push REPLACES a public menu, so nothing here
// writes to Google without the operator having been able to see exactly what would be sent. Preview
// is one click from every row, "Push now" always warns, and clearing a menu needs a second opt-in.

const LOGS_PAGE_SIZE = 25;

const primaryBtn = { background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13, color: C.white, padding: "8px 14px", cursor: "pointer" };
const secondaryBtn = { background: C.white, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13, padding: "8px 14px", cursor: "pointer" };
const labelStyle = { fontSize: 12, fontWeight: 600, color: C.textMed, marginBottom: 5, display: "block" };
const inputStyle = { width: "100%", padding: "9px 12px", fontSize: 13, borderRadius: 8, border: `1.5px solid ${C.border}`, outline: "none", color: C.text, background: C.white };
const pagerBtn = (disabled) => ({ padding: "6px 14px", borderRadius: 8, border: `1px solid ${C.border}`, background: disabled ? C.bg : C.white, color: disabled ? C.textLight : C.text, fontWeight: 600, fontSize: 12, cursor: disabled ? "default" : "pointer" });
const hint = { fontSize: 11, color: C.textLight, marginTop: 5, lineHeight: 1.5 };

const formatTime = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "-";
  const day = d.getDate();
  const month = d.toLocaleString("en-US", { month: "short" });
  let h = d.getHours();
  const m = d.getMinutes().toString().padStart(2, "0");
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${day} ${month}, ${h}:${m} ${ampm}`;
};

const Pill = ({ tone, children }) => {
  const tones = {
    good: { bg: C.greenLight, fg: C.green },
    bad: { bg: "#FDECEC", fg: "#dc3545" },
    warn: { bg: "#FFF4E5", fg: "#B26B00" },
    idle: { bg: C.borderLight, fg: C.textMed },
  };
  const t = tones[tone] || tones.idle;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: t.bg, color: t.fg }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.fg }} />
      {children}
    </span>
  );
};

/**
 * Photos, as a fraction of the items that could have one.
 *
 * Shown because photos are the usual reason a branch is not pushing: the pusher holds a menu back
 * rather than publish it with fewer pictures than the listing already shows, and without this column
 * that reads as a mysterious "media incomplete" with no way to tell whether it is progressing.
 *
 * The denominator excludes products with no image at all — they can never be satisfied, and counting
 * them would leave healthy listings looking permanently short.
 */
const PhotoCell = ({ photos, mapped }) => {
  if (!mapped || !photos) return <span style={{ color: C.textLight }}>—</span>;
  if (photos.needed === 0) return <span style={{ color: C.textLight }}>no images</span>;

  // Rows we hold beyond what this menu uses: products that left the menu, or replaced pictures whose
  // old copy is still on the listing. The nightly cleanup removes them; this is its backlog.
  const unused = Math.max(0, (photos.uploaded || 0) - photos.withPhoto);

  return (
    <div>
      {photos.missing > 0 ? (
        <Pill tone="warn">{photos.withPhoto} / {photos.needed}</Pill>
      ) : (
        <Pill tone="good">{photos.withPhoto} photos</Pill>
      )}
      {photos.missing > 0 && (
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>{photos.missing} to upload</div>
      )}
      {unused > 0 && (
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }} title="Removed by the nightly cleanup">
          {unused} unused
        </div>
      )}
    </div>
  );
};

/** A skip is not a failure — showing both as red would train operators to ignore the colour. */
const StateCell = ({ state }) => {
  if (!state) return <Pill tone="idle">Never pushed</Pill>;
  if (state.status === "success") return <Pill tone="good">Synced</Pill>;
  if (state.status === "failed") return <Pill tone="bad">Failed</Pill>;
  if (state.status === "pushing") return <Pill tone="warn">Pushing…</Pill>;
  if (state.status === "pending") return <Pill tone="warn">Queued</Pill>;
  return <Pill tone="idle">{state.skipReason ? `Skipped: ${state.skipReason.replace(/_/g, " ")}` : "Skipped"}</Pill>;
};

const GoogleBusiness = () => {
  const userType = localStorage.getItem("userType");
  const loggedInMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const merchantId = userType === "merchant" ? loggedInMerchantId : reduxSelectedMerchant;

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const [tokenInput, setTokenInput] = useState("");
  const [labelInput, setLabelInput] = useState("");
  const [savingToken, setSavingToken] = useState(false);

  const [settings, setSettings] = useState({ pushEnabled: true, dryRun: false, optionsMode: "none", menuLabel: "" });
  const [savingSettings, setSavingSettings] = useState(false);

  const [locations, setLocations] = useState(null);
  const [autoMapResult, setAutoMapResult] = useState(null);

  const [previewModal, setPreviewModal] = useState(null);
  const [restoreModal, setRestoreModal] = useState(null);
  const [restoreChoice, setRestoreChoice] = useState("previous");
  const [restoring, setRestoring] = useState(false);

  const [pushModal, setPushModal] = useState(null);
  const [pushOptions, setPushOptions] = useState({ force: false, dryRun: false, allowEmpty: false });
  const [pushing, setPushing] = useState(false);

  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsPage, setLogsPage] = useState(1);
  const [logsStatus, setLogsStatus] = useState("");
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  const integration = status?.integration || null;
  const connected = integration?.status === "connected";

  const loadStatus = useCallback(async (id) => {
    if (!id) { setStatus(null); return; }
    setLoading(true);
    const data = await getGoogleBusinessStatus(id);
    setStatus(data);
    if (data?.integration) {
      setSettings({
        pushEnabled: data.integration.pushEnabled ?? true,
        dryRun: data.integration.dryRun ?? false,
        optionsMode: data.integration.optionsMode || "none",
        menuLabel: data.integration.menuLabel || "",
      });
      setLabelInput(data.integration.connectedLabel || "");
    }
    setLoading(false);
  }, []);

  const loadLogs = useCallback(async (id, page, statusFilter) => {
    if (!id) return;
    setLogsLoading(true);
    const params = { page, limit: LOGS_PAGE_SIZE };
    if (statusFilter) params.status = statusFilter;
    const data = await getGoogleMenuPushLogs(id, params);
    setLogs(data?.logs || []);
    setLogsTotal(data?.total || 0);
    setLogsLoading(false);
  }, []);

  useEffect(() => { loadStatus(merchantId); }, [merchantId, loadStatus]);
  useEffect(() => { setLogsPage(1); }, [merchantId, logsStatus]);
  useEffect(() => { loadLogs(merchantId, logsPage, logsStatus); }, [merchantId, logsPage, logsStatus, loadLogs]);

  // The OAuth callback redirects back here with ?connected=1 or ?error=…; surface it and clean the
  // URL so a refresh does not replay the message.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("error")) errorToast(params.get("error"));
    if (params.get("connected") || params.get("error")) {
      window.history.replaceState({}, "", window.location.pathname);
      loadStatus(merchantId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    const url = await getGoogleBusinessConnectUrl(merchantId);
    if (url) window.location.href = url;
  };

  const handleImportToken = async () => {
    if (!tokenInput.trim()) { errorToast("Paste a refresh token first"); return; }
    setSavingToken(true);
    const result = await importGoogleBusinessToken(merchantId, { refreshToken: tokenInput.trim(), connectedLabel: labelInput || null });
    setSavingToken(false);
    if (result) { setTokenInput(""); loadStatus(merchantId); }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    await updateGoogleBusinessSettings(merchantId, { ...settings, connectedLabel: labelInput || null });
    setSavingSettings(false);
    loadStatus(merchantId);
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Disconnect this Google account? Menus already on Google stay as they are, but nothing further will be pushed.")) return;
    if (await disconnectGoogleBusiness(merchantId)) loadStatus(merchantId);
  };

  const handleLoadLocations = async (refresh = false) => {
    const data = await getGoogleBusinessLocations(merchantId, refresh ? { refresh: "true" } : {});
    setLocations(data);
  };

  const handleAutoMap = async (apply) => {
    const data = await autoMapGoogleLocations(merchantId, { apply });
    setAutoMapResult(data);
    if (apply) loadStatus(merchantId);
  };

  const handlePreview = async (branch) => {
    const data = await previewGoogleMenu(branch.id);
    if (data) setPreviewModal({ branch, ...data, remote: null });
  };

  const handleReadRemote = async (branch) => {
    const data = await getGoogleMenuRemote(branch.id);
    if (data) setPreviewModal((prev) => ({ ...(prev || { branch }), remote: data.foodMenus }));
  };

  const openRestoreModal = async (branch) => {
    const data = await getGoogleMenuBackups(branch.id);
    if (!data) return;
    if (!data.original && (data.backups || []).length === 0) {
      errorToast("Nothing saved for this branch yet — a copy is taken the first time we push.");
      return;
    }
    setRestoreChoice((data.backups || []).length > 0 ? "previous" : "original");
    setRestoreModal({ branch, ...data });
  };

  const handleRestore = async () => {
    setRestoring(true);
    await restoreGoogleMenu(restoreModal.branch.id, { source: restoreChoice });
    setRestoring(false);
    setRestoreModal(null);
    loadStatus(merchantId);
    loadLogs(merchantId, 1, logsStatus);
  };

  const openPushModal = (branch) => {
    setPushOptions({ force: false, dryRun: false, allowEmpty: false });
    setPushModal(branch);
  };

  const handlePush = async () => {
    setPushing(true);
    await pushGoogleMenuForBranch(pushModal.id, pushOptions);
    setPushing(false);
    setPushModal(null);
    loadStatus(merchantId);
    loadLogs(merchantId, 1, logsStatus);
  };

  const handlePushAll = async () => {
    if (!window.confirm("Queue every mapped branch for a menu push? Branches whose menu has not changed are skipped automatically.")) return;
    await pushAllGoogleMenus(merchantId, {});
    loadStatus(merchantId);
  };

  const branches = status?.branches || [];
  const mappedCount = branches.filter((b) => b.googleLocationName).length;
  // Surfaced in the header because a first sync uploads photos one at a time over several runs, and
  // an operator watching a branch refuse to push needs to see that it is working, not stuck.
  const photosPending = branches.reduce((sum, b) => sum + (b.photos?.missing || 0), 0);
  const logsTotalPages = Math.max(1, Math.ceil(logsTotal / LOGS_PAGE_SIZE));

  if (!merchantId) {
    return (
      <Fragment>
        <Breadcrumb title="Google Business" parent="Merchants" />
        <Container fluid>
          <style>{reportingGlobalCSS}</style>
          <div className="rpt-wrap">
            <RCard>
              <div style={{ textAlign: "center", padding: 40, color: C.textLight }}>
                Please select a merchant from the header.
              </div>
            </RCard>
          </div>
        </Container>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Breadcrumb title="Google Business" parent="Merchants" />
      <Container fluid>
        <style>{reportingGlobalCSS}</style>
        <div className="rpt-wrap">

          {/* ---------- Connection ---------- */}
          <RCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Google account</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {integration?.status === "connected" && <Pill tone="good">Connected</Pill>}
                {integration?.status === "needs_reauth" && <Pill tone="bad">Needs re-authorisation</Pill>}
                {(!integration || integration.status === "disconnected") && <Pill tone="idle">Not connected</Pill>}
                <button style={secondaryBtn} onClick={() => loadStatus(merchantId)}><RefreshCw size={13} /> Refresh</button>
              </div>
            </div>

            {loading ? <RLoader /> : (
              <>
                {status?.globalPushEnabled === false && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", borderRadius: 8, background: "#FFF4E5", color: "#B26B00", fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
                    <AlertTriangle size={15} />
                    Pushing is switched off server-wide (GOOGLE_MENU_PUSH_ENABLED). Nothing will reach Google until that is turned on.
                  </div>
                )}
                {integration?.lastAuthError && (
                  <div style={{ padding: "10px 12px", borderRadius: 8, background: "#FDECEC", color: "#dc3545", fontSize: 12, marginBottom: 14 }}>
                    {integration.lastAuthError}
                  </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Account label</label>
                    <input style={inputStyle} value={labelInput} onChange={(e) => setLabelInput(e.target.value)} placeholder="Whose Google login is this?" />
                    <div style={hint}>For humans only — so the next person knows which account holds these listings.</div>
                  </div>

                  <div>
                    <label style={labelStyle}>Refresh token</label>
                    <input
                      style={inputStyle}
                      type="password"
                      value={tokenInput}
                      onChange={(e) => setTokenInput(e.target.value)}
                      placeholder={integration?.hasRefreshToken ? "Stored — paste a new one to replace" : "Paste an existing refresh token"}
                      autoComplete="new-password"
                    />
                    <div style={hint}>Checked against Google before it is saved, then stored encrypted. Never shown again.</div>
                  </div>
                </div>

                <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <button style={primaryBtn} disabled={savingToken} onClick={handleImportToken}>
                    {savingToken ? "Checking…" : "Save token"}
                  </button>
                  {status?.oauthConfigured && (
                    <button style={secondaryBtn} onClick={handleConnect}>
                      <Link2 size={13} /> {connected ? "Reconnect with Google" : "Connect with Google"}
                    </button>
                  )}
                  {integration && (
                    <button style={{ ...secondaryBtn, color: "#dc3545" }} onClick={handleDisconnect}>Disconnect</button>
                  )}
                </div>
              </>
            )}
          </RCard>

          {/* ---------- Settings ---------- */}
          {integration && (
            <RCard>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 14 }}>Push settings</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
                <div>
                  <label style={labelStyle}>Push menus for this merchant</label>
                  <ToggleSwitch checked={!!settings.pushEnabled} handleToggle={(v) => setSettings((s) => ({ ...s, pushEnabled: v }))} />
                  <div style={hint}>Off means menus are still built and compared, but nothing is sent.</div>
                </div>
                <div>
                  <label style={labelStyle}>Dry run</label>
                  <ToggleSwitch checked={!!settings.dryRun} handleToggle={(v) => setSettings((s) => ({ ...s, dryRun: v }))} />
                  <div style={hint}>Builds and logs the exact payload without calling Google. The safe way to watch a new merchant for a few refreshes.</div>
                </div>
                <div>
                  <label style={labelStyle}>Item options</label>
                  <select style={inputStyle} value={settings.optionsMode} onChange={(e) => setSettings((s) => ({ ...s, optionsMode: e.target.value }))}>
                    <option value="none">Items only (recommended)</option>
                    <option value="required_variants">Required modifier group as sizes</option>
                  </select>
                  <div style={hint}>Google options are variants (one choice, replaces the price), not add-ons. Only a single required group maps cleanly.</div>
                </div>
                <div>
                  <label style={labelStyle}>Menu title</label>
                  <input style={inputStyle} value={settings.menuLabel} onChange={(e) => setSettings((s) => ({ ...s, menuLabel: e.target.value }))} placeholder="Menu" />
                  <div style={hint}>Shown as the menu heading on the Google listing.</div>
                </div>
              </div>
              <div style={{ marginTop: 16 }}>
                <button style={primaryBtn} disabled={savingSettings} onClick={handleSaveSettings}>
                  {savingSettings ? "Saving…" : "Save settings"}
                </button>
              </div>
            </RCard>
          )}

          {/* ---------- Branch mapping ---------- */}
          <RCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Branches</div>
                <div style={{ fontSize: 12, color: C.textLight, marginTop: 2 }}>
                  {mappedCount} of {branches.length} mapped to a Google listing
                  {photosPending > 0 && ` · ${photosPending} photo(s) still uploading`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {connected && <button style={secondaryBtn} onClick={() => handleLoadLocations(true)}>Load Google locations</button>}
                {connected && <button style={secondaryBtn} onClick={() => handleAutoMap(false)}>Match by store code</button>}
                {connected && mappedCount > 0 && <button style={primaryBtn} onClick={handlePushAll}><UploadCloud size={13} /> Push all</button>}
              </div>
            </div>

            {autoMapResult && (
              <div style={{ padding: 12, borderRadius: 8, background: C.bg, marginBottom: 14, fontSize: 12 }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: C.text }}>
                  {autoMapResult.applied ? "Mapped" : "Proposed"} — {autoMapResult.proposals?.length || 0} branch(es)
                </div>
                {(autoMapResult.proposals || []).map((p) => (
                  <div key={p.branchId} style={{ color: C.textMed }}>
                    {p.branchName} → {p.location.title} <span style={{ color: C.textLight }}>({p.location.name})</span>
                    {!p.location.canHaveFoodMenus && <span style={{ color: "#dc3545", fontWeight: 600 }}> — cannot hold a food menu</span>}
                  </div>
                ))}
                {!autoMapResult.applied && (autoMapResult.proposals?.length || 0) > 0 && (
                  <button style={{ ...primaryBtn, marginTop: 10 }} onClick={() => handleAutoMap(true)}>Apply these mappings</button>
                )}
              </div>
            )}

            {locations && (
              <div style={{ padding: 12, borderRadius: 8, background: C.bg, marginBottom: 14, fontSize: 12, maxHeight: 220, overflowY: "auto" }}>
                <div style={{ fontWeight: 700, marginBottom: 6, color: C.text }}>{locations.locations?.length || 0} Google location(s) visible</div>
                {(locations.locations || []).map((l) => (
                  <div key={l.name} style={{ color: C.textMed, display: "flex", justifyContent: "space-between", gap: 10 }}>
                    <span>{l.title} {l.storeCode ? <span style={{ color: C.textLight }}>· {l.storeCode}</span> : null}</span>
                    <span style={{ color: C.textLight, fontFamily: "monospace", fontSize: 11 }}>
                      {l.name}{l.mappedBranch ? ` → ${l.mappedBranch.name}` : ""}{!l.canHaveFoodMenus ? " · no menus" : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {loading ? <RLoader /> : (
              <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                <table style={tbl.table}>
                  <thead>
                    <tr>
                      <th style={tbl.th}>Branch</th>
                      <th style={tbl.th}>Google listing</th>
                      <th style={tbl.th}>Status</th>
                      <th style={tbl.th}>Last push</th>
                      <th style={tbl.th}>Items</th>
                      <th style={tbl.th}>Photos</th>
                      <th style={{ ...tbl.th, textAlign: "center" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.length === 0 ? (
                      <tr><td colSpan="7" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>No branches for this merchant.</td></tr>
                    ) : branches.map((branch) => (
                      <tr key={branch.id} className="rpt-row">
                        <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>
                          {branch.name}
                          {!branch.active && <span style={{ color: C.textLight, fontWeight: 400 }}> (inactive)</span>}
                          {branch.googleMenuPushEnabled === false && <div style={{ fontSize: 11, color: C.textLight }}>pushing paused</div>}
                        </td>
                        <td style={{ ...tbl.td, fontFamily: "monospace", fontSize: 11, color: branch.googleLocationName ? C.textMed : C.textLight }}>
                          {branch.googleLocationName || "not mapped"}
                          {branch.state?.canHaveFoodMenus === false && (
                            <div style={{ color: "#dc3545", fontFamily: "inherit", fontSize: 11, fontWeight: 600 }}>listing cannot hold a food menu</div>
                          )}
                        </td>
                        <td style={tbl.td}>
                          <StateCell state={branch.state} />
                          {branch.state?.drifted && <div style={{ marginTop: 4 }}><Pill tone="warn">Google is out of date</Pill></div>}
                          {branch.state?.lastError && (
                            <div style={{ fontSize: 11, color: "#dc3545", marginTop: 4, maxWidth: 320 }}>{branch.state.lastError}</div>
                          )}
                        </td>
                        <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>{formatTime(branch.state?.lastPushedAt)}</td>
                        <td style={tbl.td}>
                          {branch.state?.lastStats
                            ? `${branch.state.lastStats.items} in ${branch.state.lastStats.sections}`
                            : "-"}
                        </td>
                        <td style={tbl.td}>
                          <PhotoCell photos={branch.photos} mapped={!!branch.googleLocationName} />
                        </td>
                        <td style={tbl.td}>
                          <div style={{ display: "flex", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
                            <button style={secondaryBtn} onClick={() => handlePreview(branch)}><Eye size={13} /> Preview</button>
                            {branch.googleLocationName && connected && (
                              <>
                                <button style={secondaryBtn} onClick={() => handleReadRemote(branch)}>Live</button>
                                {branch.state?.hasOriginalBackup && (
                                  <button style={secondaryBtn} title="Write a saved menu back to Google" onClick={() => openRestoreModal(branch)}>
                                    <RotateCcw size={13} /> Restore
                                  </button>
                                )}
                                <button style={primaryBtn} onClick={() => openPushModal(branch)}>Push</button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </RCard>

          {/* ---------- Push logs ---------- */}
          <RCard>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>Push history</div>
              <select style={{ ...inputStyle, width: "auto" }} value={logsStatus} onChange={(e) => setLogsStatus(e.target.value)}>
                <option value="">All outcomes</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="skipped">Skipped</option>
              </select>
            </div>

            {logsLoading ? <RLoader /> : (
              <>
                <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
                  <table style={tbl.table}>
                    <thead>
                      <tr>
                        <th style={tbl.th}>When</th>
                        <th style={tbl.th}>Branch</th>
                        <th style={tbl.th}>Action</th>
                        <th style={tbl.th}>Outcome</th>
                        <th style={tbl.th}>Trigger</th>
                        <th style={tbl.th}>Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.length === 0 ? (
                        <tr><td colSpan="6" style={{ ...tbl.td, textAlign: "center", padding: 40, color: C.textLight }}>No pushes recorded yet.</td></tr>
                      ) : logs.map((log) => (
                        <tr key={log.id} className="rpt-row">
                          <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>{formatTime(log.createdAt)}</td>
                          <td style={tbl.td}>{branches.find((b) => b.id === log.branchId)?.name || log.branchId}</td>
                          <td style={tbl.td}>{log.action}</td>
                          <td style={tbl.td}>
                            <Pill tone={log.status === "success" ? "good" : log.status === "failed" ? "bad" : "idle"}>{log.status}</Pill>
                          </td>
                          <td style={tbl.td}>{log.triggeredBy}</td>
                          <td style={tbl.td}>
                            {log.error && <div style={{ color: "#dc3545", fontSize: 11, maxWidth: 360 }}>{log.error}</div>}
                            {!log.error && log.action === "patch" && `${log.itemCount ?? 0} items · ${log.durationMs ?? "?"}ms`}
                            {log.requestPayload && (
                              <button style={{ ...secondaryBtn, padding: "4px 10px", marginTop: 4 }}
                                onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}>
                                {expandedLog === log.id ? "Hide payload" : "Show payload"}
                              </button>
                            )}
                            {expandedLog === log.id && (
                              <pre style={{ marginTop: 6, maxHeight: 240, overflow: "auto", background: C.bg, padding: 10, borderRadius: 8, fontSize: 11 }}>
                                {JSON.stringify(log.requestPayload, null, 2)}
                              </pre>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: C.textLight }}>{logsTotal} entries</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button style={pagerBtn(logsPage <= 1)} disabled={logsPage <= 1} onClick={() => setLogsPage((p) => p - 1)}>Previous</button>
                    <span style={{ fontSize: 12, color: C.textMed, alignSelf: "center" }}>{logsPage} / {logsTotalPages}</span>
                    <button style={pagerBtn(logsPage >= logsTotalPages)} disabled={logsPage >= logsTotalPages} onClick={() => setLogsPage((p) => p + 1)}>Next</button>
                  </div>
                </div>
              </>
            )}
          </RCard>
        </div>
      </Container>

      {/* ---------- Preview ---------- */}
      <Modal isOpen={!!previewModal} toggle={() => setPreviewModal(null)} size="lg">
        <ModalHeader toggle={() => setPreviewModal(null)}>
          {previewModal?.remote ? "Live Google menu" : "Menu preview"} — {previewModal?.branch?.name}
        </ModalHeader>
        <ModalBody>
          {previewModal?.stats && (
            <div style={{ marginBottom: 12, fontSize: 13, color: C.textMed }}>
              <strong>{previewModal.stats.items}</strong> items in <strong>{previewModal.stats.sections}</strong> sections
              {previewModal.unchanged && <span style={{ marginLeft: 8 }}><Pill tone="good">matches what Google has</Pill></span>}
              {previewModal.stats.photos && (
                <div style={{ marginTop: 6, fontSize: 12, color: C.textMed }}>
                  Photos: <strong>{previewModal.stats.photos.withPhoto}</strong> ready
                  {previewModal.stats.photos.missing > 0 && (
                    // Said here because a push that finds photos missing uploads them first and may
                    // hold the menu back for a run or two rather than publish a thinner one.
                    <span style={{ color: "#B26B00", fontWeight: 600 }}>
                      {" "}· {previewModal.stats.photos.missing} will upload before this menu goes live
                    </span>
                  )}
                  {previewModal.stats.photos.notApplicable > 0 && (
                    <span style={{ color: C.textLight }}> · {previewModal.stats.photos.notApplicable} with no image</span>
                  )}
                </div>
              )}
              <div style={{ marginTop: 8, fontSize: 12 }}>
                {Object.entries(previewModal.stats.skipped || {})
                  .filter(([, n]) => n > 0)
                  .map(([reason, n]) => (
                    <span key={reason} style={{ marginRight: 10, color: C.textLight }}>
                      {reason.replace(/([A-Z])/g, " $1").toLowerCase()}: {n}
                    </span>
                  ))}
              </div>
            </div>
          )}
          <pre style={{ maxHeight: 440, overflow: "auto", background: C.bg, padding: 12, borderRadius: 8, fontSize: 11 }}>
            {JSON.stringify(previewModal?.remote || previewModal?.foodMenus, null, 2)}
          </pre>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPreviewModal(null)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* ---------- Restore ---------- */}
      <Modal isOpen={!!restoreModal} toggle={() => setRestoreModal(null)}>
        <ModalHeader toggle={() => setRestoreModal(null)}>Restore menu — {restoreModal?.branch?.name}</ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderRadius: 8, background: "#FFF4E5", color: "#B26B00", fontSize: 12, marginBottom: 14 }}>
            <AlertTriangle size={16} />
            This writes the saved menu back to Google and then <strong>pauses pushing for this branch</strong>,
            so the automatic sync cannot overwrite it again. Re-enable it on the branch page once the cause is fixed.
          </div>

          {(restoreModal?.backups || []).length > 0 && (
            <FormGroup check style={{ marginBottom: 10 }}>
              <Label check>
                <Input type="radio" name="restoreSource" checked={restoreChoice === "previous"} onChange={() => setRestoreChoice("previous")} />
                {" "}Previous menu — the copy taken just before the last push
                <div style={{ fontSize: 11, color: C.textLight, marginLeft: 2 }}>
                  {formatTime(restoreModal.backups[0].createdAt)} · {restoreModal.backups[0].items} items in {restoreModal.backups[0].sections} sections
                </div>
              </Label>
            </FormGroup>
          )}

          {restoreModal?.original && (
            <FormGroup check>
              <Label check>
                <Input type="radio" name="restoreSource" checked={restoreChoice === "original"} onChange={() => setRestoreChoice("original")} />
                {" "}Original menu — what the client had before this sync ever ran
                <div style={{ fontSize: 11, color: C.textLight, marginLeft: 2 }}>
                  {formatTime(restoreModal.original.at)} · {restoreModal.original.items} items in {restoreModal.original.sections} sections
                </div>
              </Label>
            </FormGroup>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRestoreModal(null)}>Cancel</Button>
          <Button color="danger" disabled={restoring} onClick={handleRestore}>
            {restoring ? "Restoring…" : "Restore and pause"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ---------- Push confirmation ---------- */}
      <Modal isOpen={!!pushModal} toggle={() => setPushModal(null)}>
        <ModalHeader toggle={() => setPushModal(null)}>Push menu — {pushModal?.name}</ModalHeader>
        <ModalBody>
          <div style={{ display: "flex", gap: 8, padding: "10px 12px", borderRadius: 8, background: "#FFF4E5", color: "#B26B00", fontSize: 12, marginBottom: 14 }}>
            <AlertTriangle size={16} />
            This replaces the menu customers see on this Google listing. Preview it first if you have not.
          </div>
          <FormGroup check style={{ marginBottom: 10 }}>
            <Label check>
              <Input type="checkbox" checked={pushOptions.dryRun} onChange={(e) => setPushOptions((o) => ({ ...o, dryRun: e.target.checked }))} />
              {" "}Dry run — build and log only, send nothing
            </Label>
          </FormGroup>
          <FormGroup check style={{ marginBottom: 10 }}>
            <Label check>
              <Input type="checkbox" checked={pushOptions.force} onChange={(e) => setPushOptions((o) => ({ ...o, force: e.target.checked }))} />
              {" "}Force — send even if nothing has changed
            </Label>
          </FormGroup>
          <FormGroup check>
            <Label check style={{ color: "#dc3545" }}>
              <Input type="checkbox" checked={pushOptions.allowEmpty} onChange={(e) => setPushOptions((o) => ({ ...o, allowEmpty: e.target.checked }))} />
              {" "}Allow an empty menu — CLEARS the listing's menu entirely
            </Label>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setPushModal(null)}>Cancel</Button>
          <Button color="primary" disabled={pushing} onClick={handlePush}>
            {pushing ? "Pushing…" : pushOptions.dryRun ? "Run dry" : "Push to Google"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default GoogleBusiness;
